<template>
    <div class="authorize-page">
        <!-- Decorative background -->
        <div class="authorize-bg" aria-hidden="true">
            <span class="authorize-bg__orb _authorize-bg__orb--blue"></span>
            <span class="authorize-bg__orb _authorize-bg__orb--orange"></span>
        </div>

        <div class="authorize-container">
            <!-- Logo -->
            <div class="authorize-logo">
                <logo type="without-text" />
            </div>

            <!-- Card -->
            <div v-if="App && App.isPublished" class="authorize-card">
                <!-- User -->
                <div class="authorize-user">
                    <div class="authorize-user__avatar">{{ userInitials }}</div>
                    <div class="authorize-user__details">
                        <span class="authorize-user__name">{{ UserProfile.firstName }} {{ UserProfile.lastName }}</span>
                        <span class="authorize-user__email">{{ UserProfile.email }}</span>
                    </div>
                </div>

                <!-- Message -->
                <p class="authorize-message">
                    Vous êtes connecté et serez redirigé vers
                    <strong>{{ App.name }}</strong> automatiquement.
                </p>

                <!-- Countdown -->
                <div class="authorize-countdown">
                    <svg class="authorize-countdown__ring" viewBox="0 0 100 100">
                        <defs>
                            <linearGradient id="ocCountdownGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stop-color="#05348E" />
                                <stop offset="100%" stop-color="#FF6600" />
                            </linearGradient>
                        </defs>
                        <circle class="authorize-countdown__track" cx="50" cy="50" r="45" />
                        <circle
                            class="authorize-countdown__progress"
                            cx="50"
                            cy="50"
                            r="45"
                            :stroke-dasharray="circumference"
                            :stroke-dashoffset="countdownOffset"
                        />
                    </svg>
                    <span class="authorize-countdown__number">{{ countdown }}</span>
                </div>

                <!-- Button -->
                <button
                    class="authorize-btn"
                    :class="{ 'authorize-btn--loading': resendLoading }"
                    :disabled="resendLoading"
                    @click="makeRequest"
                >
                    <span v-if="resendLoading" class="authorize-btn__spinner" aria-hidden="true"></span>
                    <span class="authorize-btn__label">{{ resendLoading ? 'Redirection...' : 'Continuer' }}</span>
                </button>
            </div>

            <!-- Loading -->
            <div v-else class="authorize-card authorize-card--loading">
                <div class="authorize-spinner"></div>
                <p class="authorize-loading-text">Chargement...</p>
            </div>
        </div>
    </div>
</template>

<script>
import Logo from "@/components/identifier/AuthLogo.vue";
import { requestToSharedAuthInfoWithApp } from "@/api/user";
import { methods } from "@/utils/constant.js";
import { mapGetters, mapActions } from "vuex";

export default {
    components: { Logo },
    data() {
        return {
            loading: false,
            resendLoading: false,
            appName: process.env.APP_NAME || 'OrangCode',
            clientId: localStorage.getItem("target_app_client_id"),
            redirectUri: localStorage.getItem("target_app_redirect_uri"),
            nextLink: localStorage.getItem("target_app_next_link"),
            initialCountdown: 3,
            countdown: 3,
            countdownTimer: null,
        };
    },
    computed: {
        ...mapGetters(['App', "UserProfile"]),
        userInitials() {
            const profile = this.UserProfile
            const first = profile && profile.firstName ? profile.firstName.charAt(0) : ''
            const last = profile && profile.lastName ? profile.lastName.charAt(0) : ''
            return (first + last).toUpperCase() || '?'
        },
        // Circumference of the countdown ring (r = 45, matching the SVG circle).
        circumference() {
            return 2 * Math.PI * 45
        },
        // Depletes the ring in sync with the countdown value.
        countdownOffset() {
            const total = this.initialCountdown
            const progress = total > 0 ? this.countdown / total : 0
            return this.circumference * (1 - progress)
        }
    },
    methods: {
        ...mapActions(['fetchApp', "getUserProfile"]),

        async initializeApp() {
            try {
                localStorage.setItem("redirect-to-user-account", '');
                await this.getUserProfile({})
                await this.fetchApp({ clientId: this.clientId })
                this.startCountdown()
            } catch (error) {
                const message = error && error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : "Something went wrong"
                methods.dispatchError(this.$store, message);
            }
        },

        startCountdown() {
            this.countdownTimer = setInterval(() => {
                this.countdown--
                if (this.countdown <= 0) {
                    this.stopCountdown()
                    this.makeRequest()
                }
            }, 1000)
        },

        stopCountdown() {
            if (this.countdownTimer) {
                clearInterval(this.countdownTimer)
                this.countdownTimer = null
            }
        },

        async makeRequest() {
            this.stopCountdown()
            this.resendLoading = true

            try {
                await requestToSharedAuthInfoWithApp({
                    clientId: this.clientId,
                    redirectUri: this.redirectUri,
                    nextLink: this.nextLink,
                });
            } catch (error) {
                const message = error && error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : "Something went wrong"
                methods.dispatchError(this.$store, message);
                this.resendLoading = false
            }
        },

        cleanLocal() {
            localStorage.setItem("target_app_client_id", '')
            localStorage.setItem("target_app_redirect_uri", '')
            localStorage.setItem("target_app_next_link", '')
        }
    },
    beforeDestroy() {
        this.stopCountdown()
    },
    beforeRouteLeave(to, from, next) {
        this.cleanLocal()
        next()
    },
    mounted() {
        this.initializeApp()
    }
};
</script>

<style scoped>
.authorize-page {
    --oc-primary: #05348E;
    --oc-primary-2: #0a47b8;
    --oc-primary-dark: #042a73;
    --oc-secondary: #FF6600;
    --oc-ink: #16233b;
    --oc-muted: #64748b;

    position: relative;
    min-height: 100vh;
    min-height: 100dvh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    overflow: hidden;
    background: linear-gradient(160deg, #f6f8fc 0%, #eef2f9 100%);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
}

.authorize-page *,
.authorize-page *::before,
.authorize-page *::after {
    box-sizing: border-box;
}

/* Background orbs (give the frosted card something to blur) */
.authorize-bg {
    position: absolute;
    inset: 0;
    pointer-events: none;
}

.authorize-bg__orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(90px);
    opacity: 0.4;
}

.authorize-bg__orb--blue {
    width: 400px;
    height: 400px;
    background: var(--oc-primary);
    top: -130px;
    left: -110px;
}

.authorize-bg__orb--orange {
    width: 340px;
    height: 340px;
    background: var(--oc-secondary);
    bottom: -130px;
    right: -90px;
}

.authorize-container {
    position: relative;
    width: 100%;
    max-width: 400px;
}

.authorize-logo {
    display: flex;
    justify-content: center;
    margin-bottom: 28px;
}

/* Glass card */
.authorize-card {
    position: relative;
    padding: 32px 28px;
    background: rgba(255, 255, 255, 0.72);
    -webkit-backdrop-filter: blur(20px) saturate(160%);
    backdrop-filter: blur(20px) saturate(160%);
    border: 1px solid rgba(255, 255, 255, 0.6);
    border-radius: 20px;
    box-shadow:
        0 20px 60px -20px rgba(5, 52, 142, 0.28),
        0 2px 8px rgba(16, 35, 59, 0.06);
    animation: authorize-rise 0.5s ease;
}

.authorize-card--loading {
    padding: 56px 28px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}

@keyframes authorize-rise {
    from {
        opacity: 0;
        transform: translateY(16px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Big loading spinner */
.authorize-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(5, 52, 142, 0.12);
    border-top-color: var(--oc-secondary);
    border-radius: 50%;
    animation: authorize-spin 0.8s linear infinite;
}

.authorize-loading-text {
    margin: 0;
    font-size: 14px;
    color: var(--oc-muted);
}

@keyframes authorize-spin {
    to {
        transform: rotate(360deg);
    }
}

/* User */
.authorize-user {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    background: rgba(5, 52, 142, 0.05);
    border: 1px solid rgba(5, 52, 142, 0.08);
    border-radius: 14px;
    margin-bottom: 22px;
    text-align: left;
}

.authorize-user__avatar {
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, var(--oc-primary), var(--oc-primary-2));
    border-radius: 50%;
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    box-shadow: 0 4px 12px rgba(5, 52, 142, 0.25);
}

.authorize-user__details {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.authorize-user__name,
.authorize-user__email {
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.authorize-user__name {
    font-size: 14.5px;
    font-weight: 600;
    color: var(--oc-ink);
}

.authorize-user__email {
    font-size: 13px;
    color: var(--oc-muted);
}

/* Message */
.authorize-message {
    margin: 0 0 24px;
    font-size: 14px;
    line-height: 1.6;
    color: var(--oc-muted);
    text-align: center;
}

.authorize-message strong {
    color: var(--oc-primary);
    font-weight: 600;
}

/* Countdown ring (signature element) */
.authorize-countdown {
    position: relative;
    width: 84px;
    height: 84px;
    margin: 0 auto 26px;
}

.authorize-countdown__ring {
    width: 100%;
    height: 100%;
    transform: rotate(-90deg);
}

.authorize-countdown__track {
    fill: none;
    stroke: rgba(5, 52, 142, 0.1);
    stroke-width: 6;
}

.authorize-countdown__progress {
    fill: none;
    stroke: url(#ocCountdownGradient);
    stroke-width: 6;
    stroke-linecap: round;
    transition: stroke-dashoffset 1s linear;
}

.authorize-countdown__number {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    font-weight: 700;
    color: var(--oc-primary);
}

/* Button */
.authorize-btn {
    width: 100%;
    height: 48px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: linear-gradient(135deg, var(--oc-primary), var(--oc-primary-2));
    color: #fff;
    border: none;
    border-radius: 12px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 8px 20px -8px rgba(5, 52, 142, 0.6);
    transition: transform 0.15s ease, box-shadow 0.2s ease, filter 0.2s ease;
}

.authorize-btn:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.05);
    box-shadow: 0 12px 26px -8px rgba(5, 52, 142, 0.7);
}

.authorize-btn:active:not(:disabled) {
    transform: translateY(0);
}

.authorize-btn:focus-visible {
    outline: 3px solid rgba(255, 102, 0, 0.5);
    outline-offset: 2px;
}

.authorize-btn:disabled {
    opacity: 0.75;
    cursor: default;
}

.authorize-btn__spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: #fff;
    border-radius: 50%;
    animation: authorize-spin 0.7s linear infinite;
}

/* Small screens */
@media (max-width: 380px) {
    .authorize-card {
        padding: 26px 20px;
    }
}

/* Accessibility: respect reduced motion */
@media (prefers-reduced-motion: reduce) {
    .authorize-card {
        animation: none;
    }
    .authorize-countdown__progress,
    .authorize-btn {
        transition: none;
    }
    .authorize-btn:hover:not(:disabled) {
        transform: none;
    }
}
</style>